import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import classNames from 'classnames';

// components
import { PdpSharedShippingModalLoader } from '../PdpSharedModals/PdpSharedShippingModal/PdpSharedShippingModalLoader';
import { viewOptions, requestQuote, viewRequest } from '../messages/pdpShippingLinkMessages';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';

// styles
import styles from './PdpSharedShippingInfo.scss';

// helpers
import tracking from 'dibs-tracking';
import { getQueryParam } from '../../utils/uriUtils';
import get from 'lodash.get';

import { sharedLoginCheck } from '../../authentication/sharedLoginCheck';

import { isEmailOnly } from 'dibs-cookie-jar/src/helpers/emailOnlyHelpers';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

export class PdpSharedShippingInfoLinkComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = { modalOpen: false, hasLoggedIn: false, address: null };
        this.openShippingModal = this.openShippingModal.bind(this);
        this.closeShippingModal = this.closeShippingModal.bind(this);
        this.showAuthModal = this.showAuthModal.bind(this);
        this.showCompleteAccount = this.showCompleteAccount.bind(this);
    }

    componentDidMount() {
        const modalParam = getQueryParam(get(global, 'location.search'), 'modal');
        if (modalParam === 'shipping') {
            this.openShippingModal();
        }
    }

    showAuthModal({ onSuccess, onError } = {}) {
        sharedLoginCheck({
            onSuccess,
            onError,
            authOptions: {
                flow: authModalLoader.constants.REQUEST_SHIPPING_FLOW,
            },
        });
    }
    showCompleteAccount() {
        const { user } = this.props;
        const email = get(user, 'profile.email');
        authModalLoader
            .show({
                action: authModalLoader.constants.REQUEST_PASSWORD_EMAIL,
                flow: authModalLoader.constants.CREATE_PASSWORD_REQUEST_SHIPPING_MC_FLOW,
                email,
                relayEnvironment: this.props.relay.environment,
            })
            .then(({ userId }) => {
                if (userId) {
                    this.openShippingModal();
                }
            });
    }

    openShippingModal(e, status) {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }

        const { userId } = this.props;

        //handle login
        if (!userId && status === 'requestQuote') {
            this.showAuthModal({
                onSuccess: this.openShippingModal,
            });
        } else if (isEmailOnly(document.cookie) && status === 'requestQuote') {
            this.showCompleteAccount();
        } else {
            this.setState({ modalOpen: true }, () => {
                tracking.trackEvent({
                    category: 'contact dealer',
                    action: 'contact dealer clicked',
                    label: 'request a shipping quote - pdp sidebar or details',
                });
            });
        }
    }

    closeShippingModal() {
        this.setState({ modalOpen: false });
    }

    render() {
        const {
            viewer,
            currency,
            userId,
            itemId,
            user,
            item,
            showQuoteDetails,
            quoteRequestStatus,
            relay,
            onSuccessfulQuoteRequest,
            displayInline,
        } = this.props;

        //enabler code
        const canRequestShippingQuotePermission = get(user, 'canRequestShippingQuotePermission');
        const onlyTradeRequestsQuote = false;
        const blockRequest = onlyTradeRequestsQuote && !canRequestShippingQuotePermission;

        const itemIsOnHold = get(this.props, 'item.itemIsOnHold');
        const hasPrivateListing = get(this.props, 'item.hasPrivateListing');
        const hasQuote = get(this.props, 'item.hasShipmentQuotes');
        const conversationId = get(viewer, 'buyerItemConversation.serviceId');
        const pendingRedirect = `/my/conversations/${conversationId}/`;
        const shouldHideLink = hasPrivateListing && quoteRequestStatus === 'requestQuote';

        if (shouldHideLink) {
            return null;
        }
        // link cta
        let linkCta;
        if (quoteRequestStatus === 'customQuoteRequested') {
            linkCta = viewRequest;
        } else if (
            quoteRequestStatus === 'customQuoteProvided' ||
            quoteRequestStatus === 'viewQuotesNoCustom' ||
            ((itemIsOnHold || blockRequest) && quoteRequestStatus === 'viewQuotesNoCustom')
        ) {
            linkCta = viewOptions;
        } else {
            linkCta = requestQuote;
        }

        // link component
        let linkComponent;
        if (
            (itemIsOnHold || blockRequest) &&
            quoteRequestStatus !== 'viewQuotesNoCustom' &&
            quoteRequestStatus !== 'customQuoteRequested'
        ) {
            linkComponent = (
                <PdpSharedContactDealerButton source="shipment-info-link" user={user} item={item} />
            );
        } else if (quoteRequestStatus === 'customQuoteRequested') {
            linkComponent = (
                <a href={pendingRedirect} className={styles.shippingLink} data-tn="view-request">
                    {linkCta}
                </a>
            );
        } else {
            linkComponent = (
                <a
                    href="#shipping-modal"
                    onClick={event => {
                        this.openShippingModal(event, quoteRequestStatus);
                    }}
                    className={styles.shippingLink}
                    data-tn="open-modal"
                >
                    {linkCta}
                </a>
            );
        }

        const containerClass = classNames(styles.shippingModalLink, {
            [styles.displayInline]: displayInline,
        });

        return (
            <div className={containerClass}>
                {linkComponent}
                <div>
                    <PdpSharedShippingModalLoader
                        currency={currency}
                        userId={userId}
                        itemId={itemId}
                        environment={relay.environment}
                        hasQuote={hasQuote}
                        showQuoteDetails={showQuoteDetails}
                        isOpened={this.state.modalOpen}
                        closeShippingModal={this.closeShippingModal}
                        trackingLocation="pdp sidebar"
                        showAuthModal={this.showAuthModal}
                        showCompleteAccount={this.showCompleteAccount}
                        openModal={this.openShippingModal}
                        onSuccess={onSuccessfulQuoteRequest}
                    />
                </div>
            </div>
        );
    }
}

PdpSharedShippingInfoLinkComponent.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    relay: PropTypes.object.isRequired,
    showQuoteDetails: PropTypes.bool.isRequired,
    quoteRequestStatus: PropTypes.string,
    onSuccessfulQuoteRequest: PropTypes.func,
    displayInline: PropTypes.bool,
};

PdpSharedShippingInfoLinkComponent.defaultProps = {
    onSuccessfulQuoteRequest: () => {},
};

export const PdpSharedShippingInfoLink = createFragmentContainer(
    PdpSharedShippingInfoLinkComponent,
    {
        viewer: graphql`
            fragment PdpSharedShippingInfoLink_viewer on Viewer {
                buyerItemConversation(buyerId: $userId, itemId: $itemId) @include(if: $fetchUser) {
                    serviceId
                }
            }
        `,
        user: graphql`
            fragment PdpSharedShippingInfoLink_user on User {
                ...PdpSharedContactDealerButton_user
                canRequestShippingQuotePermission: hasPermission(name: "B_REQUEST_SHIPPING_QUOTE")
                profile {
                    email
                }
            }
        `,
        item: graphql`
            fragment PdpSharedShippingInfoLink_item on Item {
                ...PdpSharedContactDealerButton_item
                hasShipmentQuotes
                itemIsOnHold: isOnHold(buyerId: $userId)
                hasPrivateListing
                seller {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
                attributes {
                    leadTime {
                        value
                    }
                }
            }
        `,
        shipmentQuote: graphql`
            fragment PdpSharedShippingInfoLink_shipmentQuote on ShipmentQuote {
                isCustom
                isNew
                status
            }
        `,
    }
);
